import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const AccessibilityStatement = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            Back to Home
          </Button>
        </Link>

        <h1 className="text-4xl font-bold mb-4">Accessibility Statement</h1>
        <p className="text-muted-foreground mb-8">Effective date: August 21, 2026</p>

        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">Our commitment</h2>
            <p className="text-muted-foreground leading-relaxed">
              TopServ Digital is committed to making the Brand Equity Analyzer usable by as many people as
              possible, including people who use screen readers, keyboard-only navigation, magnification, or
              other assistive technology. We are working in good faith toward the Web Content Accessibility
              Guidelines (WCAG) 2.2 Level AA as our target standard. We do not claim full conformance, and we
              treat accessibility as ongoing work.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">What we have implemented</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>A "Skip to main content" link as the first focusable element on every page</li>
              <li>Semantic landmarks: one banner header, one main region per page, and a footer</li>
              <li>Visible keyboard focus indicators on interactive controls</li>
              <li>Labels on all form fields, with errors announced in text rather than color alone</li>
              <li>Accessible names on icon-only buttons</li>
              <li>Text alternatives for meaningful images and decorative images marked as decorative</li>
              <li>Support for the operating system's "reduce motion" preference</li>
              <li>A responsive layout intended to reflow when you zoom in or use a small screen</li>
              <li>Accessible components built on the Radix UI primitives for dialogs, menus, and tooltips</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Known limitations</h2>
            <p className="text-muted-foreground leading-relaxed">
              Some report visualizations are presented as charts, and not every chart currently has a text or
              table equivalent. If you cannot access a specific chart, contact us and we will provide the
              underlying numbers in an accessible format. Third-party data sources and embedded
              provider content may not fully meet our target standard, and we do not control them.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Alternate access and feedback</h2>
            <p className="text-muted-foreground leading-relaxed">
              If any part of this site creates a barrier for you, or if you would like a report or a form
              completed in an alternate format, email{" "}
              <a href="mailto:privacy@topservdigital.com" className="underline text-brand-700">
                privacy@topservdigital.com
              </a>{" "}
              with the subject line "Accessibility". Please include the page address and a short description of
              the problem and the assistive technology you use. We review accessibility requests as promptly as we
              reasonably can and will offer an alternate way to complete any task you cannot finish on the site.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Assessment approach</h2>
            <p className="text-muted-foreground leading-relaxed">
              We evaluate the Service through internal review and automated accessibility checks during
              development. We have not commissioned an independent third-party audit or certification. This
              statement is reviewed when we make significant changes to the Service.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityStatement;
